import { Component, Input, Output, EventEmitter, type OnInit, inject, OnChanges, SimpleChanges, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, type FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { QuillModule } from "ngx-quill";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { environment } from "src/environments/environment";

export const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],

    [{ 'header': 1 }, { 'header': 2 }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'script': 'sub' }, { 'script': 'super' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    [{ 'direction': 'rtl' }],

    [{ 'size': ['small', false, 'large', 'huge'] }],
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

    [{ 'color': [] }, { 'background': [] }],
    [{ 'font': [] }],
    [{ 'align': [] }],

    ['clean'],

    ['link']
  ]
};

@Component({
  selector: "app-basic-info-step",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, QuillModule],
  templateUrl: "./basic-info-step.component.html",
  styleUrls: ["./basic-info-step.component.css"],
})
export class BasicInfoStepComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public isReadonly: boolean = false;
  @Input() formData: any = {};
  @Output() dataChange = new EventEmitter<any>();

  coverFileId: number | null = null;
  coverPreview: SafeResourceUrl | null = null;
  coverFile: SafeResourceUrl | null = null;

  private fb = inject(FormBuilder);
  public readonly sanitizer = inject(DomSanitizer);

  form!: FormGroup;
  private destroy$ = new Subject<void>();
  private syncingFromInput = false;

  fileBaseUrl = `${environment.api.url}/File/ViewFile`;

  quillOptions = quillModules;

  ngOnInit() {
    this.buildForm(this.formData);
    this.coverFileId = this.formData?.coverFileId;
    this.coverFile = this.formData?.coverFile
      ? this.sanitizer.bypassSecurityTrustResourceUrl(this.formData.coverFile)
      : null;
    this.coverPreview = this.formData?.coverPreview
      ? this.sanitizer.bypassSecurityTrustResourceUrl(this.formData.coverPreview)
      : null;
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (this.syncingFromInput) return;
        this.dataChange.emit(value);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes["formData"]) return;
    const v = changes["formData"].currentValue || {};

    if (!this.form) return;

    this.syncingFromInput = true;
    this.form.patchValue(
      {
        title: v.title ?? "",
        subtitle: v.subtitle ?? "",
        shortDescription: v.shortDescription ?? "",
        longDescription: v.longDescription ?? "",
        coverFile: v.coverFile ?? null,
        coverFileId: v?.coverFileId ?? null
      },
      { emitEvent: false }
    );
    this.coverFileId = this.formData?.coverFileId;
    this.coverFile = v.coverFile
      ? this.sanitizer.bypassSecurityTrustResourceUrl(v.coverFile)
      : null;

    this.syncingFromInput = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm(src: any) {
    this.form = this.fb.group({
      id: [src?.id],
      title: [src?.title || "", [Validators.required, Validators.maxLength(200)]],
      subtitle: [src?.subtitle || "", [Validators.required, Validators.maxLength(300)]],
      shortDescription: [src?.shortDescription || "", [Validators.required, Validators.maxLength(600)]],
      longDescription: [src?.longDescription || ""],
      coverFile: [src?.coverFile || null],
      coverFileId: [src?.coverFileId]
    });
  }

  setCoverFile(base64: string | null) {
    this.syncingFromInput = true;
    this.form.get("coverFile")?.setValue(base64, { emitEvent: false });
    this.coverFile = base64
      ? this.sanitizer.bypassSecurityTrustResourceUrl(base64)
      : null;
    this.syncingFromInput = false;

    this.dataChange.emit(this.form.value);
  }

  validate() {
    console.log(this.form)
    const isValid = this.form.valid;
    if (!isValid) this.form.markAllAsTouched();
    return isValid;
  }
}